/* global initLazyImages */

if (!customElements.get('home-product-feed')) {
  class HomeProductFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.batchSize = Number.parseInt(this.dataset.batchSize, 10) || 16;
      this.initialSize = Number.parseInt(this.dataset.initialSize, 10) || this.batchSize;
      this.mobileBatchSize = Number.parseInt(this.dataset.mobileBatchSize, 10) || 8;
      this.mobileAutoLimit = Number.parseInt(this.dataset.mobileAutoLimit, 10) || 64;
      this.hideSoldOut = this.dataset.hideSoldOut !== 'false';
      this.layout = this.dataset.layout || 'grid';
      this.mobileCarousel = this.dataset.mobileCarousel === 'true';
      this.mobileAutoLoad = this.dataset.mobileAutoLoad === 'true';
      this.singleCollection = this.dataset.singleCollection === 'true';
      this.showRankBadges = this.dataset.showRankBadges === 'true';
      this.instanceKey = (this.dataset.feedInstance || 'feed').replace(/[^a-zA-Z0-9_-]/g, '-');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panel = this.querySelector('[role="tabpanel"]');
      this.grid = this.querySelector('.js-home-feed-grid');
      this.skeleton = this.querySelector('.js-home-feed-skeleton');
      this.loadMoreButton = this.querySelector('.js-home-feed-load-more');
      this.viewAllLink = this.querySelector('.js-home-feed-view-all');
      this.actions = this.querySelector('.js-home-feed-actions');
      this.emptyMessage = this.querySelector('.js-home-feed-empty');
      this.errorMessage = this.querySelector('.js-home-feed-error');
      this.retryButton = this.querySelector('.js-home-feed-retry');
      this.errorCollectionLink = this.querySelector('.js-home-feed-error-link');
      this.collectionLink = this.querySelector('.js-home-feed-collection-link');
      this.previousButton = this.querySelector('.js-home-feed-prev');
      this.nextButton = this.querySelector('.js-home-feed-next');
      this.status = this.querySelector('.js-home-feed-status');
      this.states = new Map();
      this.hasActivated = false;
      this.hasSettled = false;
      this.mobileQuery = window.matchMedia('(max-width: 768.98px)');
      this.mobileLoadSequence = 0;

      this.tabs.forEach((tab) => {
        const key = tab.dataset.feedKey;
        this.states.set(key, {
          key,
          label: tab.dataset.feedLabel || tab.textContent.trim(),
          collectionUrl: tab.dataset.collectionUrl,
          initialized: false,
          nextUrl: tab.dataset.endpoint,
          queue: [],
          fragment: document.createDocumentFragment(),
          renderedCount: 0,
          seenIds: new Set(),
          controller: null,
          fetchPromise: null,
          intentTimer: null,
          idSequence: 0,
          scrollPosition: 0
        });
        tab.addEventListener('click', (event) => this.selectTab(event.currentTarget));
        tab.addEventListener('keydown', (event) => this.handleTabKeydown(event));
        tab.addEventListener('pointerenter', (event) => this.handleTabIntent(event));
        tab.addEventListener('pointerleave', (event) => this.cancelTabIntent(event.currentTarget));
        tab.addEventListener('focus', (event) => this.handleTabIntent(event));
        tab.addEventListener('blur', (event) => this.cancelTabIntent(event.currentTarget));
      });

      this.loadMoreButton?.addEventListener('click', () => this.handleLoadMore());
      this.retryButton?.addEventListener('click', () => this.handleRetry());
      this.previousButton?.addEventListener('click', () => this.scrollShelf(-1));
      this.nextButton?.addEventListener('click', () => this.scrollShelf(1));
      this.grid?.addEventListener('scroll', () => this.handleGridScroll(), { passive: true });
      this.handleMobileQueryChange = () => this.updateResponsiveLoadingMode();
      this.mobileQuery.addEventListener?.('change', this.handleMobileQueryChange);
      if ('ResizeObserver' in window && this.grid) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateShelfNavigation();
          this.scheduleMobileAutoLoad();
        });
        this.resizeObserver.observe(this.grid);
      }

      this.activeTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      if (this.activeTab) this.updateActiveTabUi(this.activeTab);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => this.handleIntersection(entries), {
          rootMargin: '0px 0px 50% 0px',
          threshold: 0.01
        });
        this.observer.observe(this);
      } else {
        this.activateFeed();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.revealFrame) window.cancelAnimationFrame(this.revealFrame);
      if (this.mobileLoadFrame) window.cancelAnimationFrame(this.mobileLoadFrame);
      this.mobileQuery?.removeEventListener?.('change', this.handleMobileQueryChange);
      this.mobileLoadSequence += 1;
      this.hideMobileLoadingTile();
      this.states?.forEach((state) => {
        state.controller?.abort();
        if (state.intentTimer) window.clearTimeout(state.intentTimer);
      });
    }

    handleIntersection(entries) {
      if (entries.some((entry) => entry.isIntersecting)) this.activateFeed();
    }

    activateFeed() {
      if (this.hasActivated || !this.activeTab) return;
      this.hasActivated = true;
      this.observer?.disconnect();
      this.loadBatch(this.activeTab.dataset.feedKey);
    }

    handleTabIntent(event) {
      const tab = event.currentTarget;
      if (!tab || tab === this.activeTab || !this.canPrefetch()) return;
      const state = this.states.get(tab.dataset.feedKey);
      if (!state || state.initialized || state.fetchPromise) return;
      this.cancelTabIntent(tab);
      const delay = event.type === 'focus' ? 0 : 120;
      state.intentTimer = window.setTimeout(() => {
        state.intentTimer = null;
        this.fetchPage(state).catch(() => {});
      }, delay);
    }

    cancelTabIntent(tab) {
      const state = this.states.get(tab?.dataset.feedKey);
      if (!state?.intentTimer) return;
      window.clearTimeout(state.intentTimer);
      state.intentTimer = null;
    }

    canPrefetch() {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!connection) return true;
      return !connection.saveData && (!connection.effectiveType || connection.effectiveType === '4g');
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let targetIndex = null;

      if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = this.tabs.length - 1;
      if (targetIndex === null) return;

      event.preventDefault();
      this.tabs[targetIndex].focus();
      this.selectTab(this.tabs[targetIndex]);
    }

    selectTab(tab) {
      if (!tab || tab === this.activeTab) {
        this.activateFeed();
        return;
      }

      // Let the previous tab's bounded request finish into its in-memory queue.
      // Aborting it here leaves a rapid A -> B -> A switch waiting on a rejected
      // promise with no follow-up request.
      this.mobileLoadSequence += 1;
      this.hideMobileLoadingTile();
      this.setBusy(false);
      this.saveCurrentState();
      this.activeTab = tab;
      this.hasActivated = true;
      this.observer?.disconnect();
      this.updateActiveTabUi(tab);
      this.renderState(tab.dataset.feedKey);
    }

    updateActiveTabUi(tab) {
      this.tabs.forEach((item) => {
        const isActive = item === tab;
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
      });

      if (!this.singleCollection) this.panel?.setAttribute('aria-labelledby', tab.id);
      if (this.collectionLink) this.collectionLink.href = tab.dataset.collectionUrl;
      if (this.viewAllLink) this.viewAllLink.href = tab.dataset.collectionUrl;
    }

    saveCurrentState() {
      if (!this.activeTab || !this.grid) return;
      const state = this.states.get(this.activeTab.dataset.feedKey);
      if (!state || !state.initialized) return;
      state.scrollPosition = this.grid.scrollLeft;
      state.fragment.append(...this.getRenderedCards());
      state.renderedCount = state.fragment.childElementCount;
    }

    renderState(key) {
      const state = this.states.get(key);
      this.hideMessages();

      if (state?.fragment.childElementCount) {
        this.grid.replaceChildren(state.fragment);
        this.grid.hidden = false;
        this.hasSettled = true;
        this.skeleton.hidden = true;
        this.restoreMobileCarouselPosition(state.scrollPosition);
        window.initLazyImages?.();
        this.updateLoadMore(state);
        this.updateShelfNavigation(true);
        this.announce(`${state.renderedCount} products shown`);
        return;
      }

      this.grid.replaceChildren();
      this.grid.hidden = true;
      this.loadBatch(key);
    }

    async handleLoadMore() {
      if (!this.activeTab) return;
      await this.loadBatch(this.activeTab.dataset.feedKey, true);
    }

    async handleRetry() {
      if (!this.activeTab) return;
      this.errorMessage.hidden = true;
      const key = this.activeTab.dataset.feedKey;
      const append = this.getRenderedCards().length > 0;
      if (append && this.isProgressiveLoadMode()) {
        await this.loadMobileBatch(key);
        return;
      }
      await this.loadBatch(key, append);
    }

    async loadBatch(key, append = false, { mobileAuto = false } = {}) {
      const state = this.states.get(key);
      if (!state || this.getAttribute('aria-busy') === 'true') return;

      const remainingMobileSlots = Math.max(0, this.mobileAutoLimit - state.renderedCount);
      const appendSize = mobileAuto
        ? Math.min(this.mobileBatchSize, remainingMobileSlots)
        : this.batchSize;
      const requestedSize = append || state.renderedCount > 0 ? appendSize : this.initialSize;
      if (requestedSize === 0) {
        this.updateLoadMore(state);
        return;
      }
      const scrollLeftBeforeRequest = append && this.grid ? this.grid.scrollLeft : 0;
      this.setBusy(true, state.renderedCount === 0);
      this.hideMessages();

      try {
        let attempts = 0;
        while (state.queue.length < requestedSize && state.nextUrl && attempts < 10) {
          await this.fetchPage(state);
          attempts += 1;
        }

        if (this.activeTab?.dataset.feedKey !== key) return;
        const nextCards = state.queue.splice(0, requestedSize);
        if (nextCards.length === 0 && state.renderedCount === 0) {
          this.showEmpty(state);
          return;
        }

        const firstNewCard = nextCards[0];
        if (!append) this.grid.replaceChildren();
        const loadingTile = this.mobileLoadingTile?.isConnected ? this.mobileLoadingTile : null;
        nextCards.forEach((card) => this.grid.insertBefore(card, loadingTile));
        this.grid.hidden = false;
        this.hasSettled = true;
        state.renderedCount = this.getRenderedCards().length;
        if (!append) this.resetMobileCarouselPosition();
        window.initLazyImages?.();
        this.updateLoadMore(state);
        this.updateShelfNavigation(true);
        const reachedMobileLimit = mobileAuto
          && state.renderedCount >= this.mobileAutoLimit
          && (state.queue.length > 0 || Boolean(state.nextUrl));
        this.announce(append
          ? `${nextCards.length} more products added; ${state.renderedCount} shown${reachedMobileLimit ? '; view all to browse the full collection' : ''}`
          : `${state.renderedCount} products shown`);
        if (append && firstNewCard && !mobileAuto) this.revealAppendedProducts(firstNewCard, scrollLeftBeforeRequest);
        this.scheduleMobileAutoLoad();
      } catch (error) {
        if (error.name !== 'AbortError' && this.activeTab?.dataset.feedKey === key) this.showError(state);
      } finally {
        if (this.activeTab?.dataset.feedKey === key) this.setBusy(false);
      }
    }

    fetchPage(state) {
      if (state.fetchPromise) return state.fetchPromise;
      if (!state.nextUrl) return Promise.resolve();

      state.controller = new AbortController();
      state.fetchPromise = (async () => {
        const response = await fetch(state.nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: state.controller.signal
        });
        if (!response.ok) throw new Error(`Feed request failed: ${response.status}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const results = template.content.querySelector('.home-product-feed-results');
        if (!results) throw new Error('Feed response was missing product results.');

        const excludedIds = this.getPageProductIds();
        results.querySelectorAll('[data-feed-card]').forEach((card) => {
          const productId = card.dataset.productId;
          const canShow = !this.hideSoldOut || card.dataset.available === 'true';
          if (!productId || !canShow || state.seenIds.has(productId) || excludedIds.has(productId)) return;
          state.seenIds.add(productId);
          this.prepareCard(card, state);
          state.queue.push(card);
        });

        state.nextUrl = results.dataset.nextUrl || '';
        state.initialized = true;
      })().finally(() => {
        state.fetchPromise = null;
        state.controller = null;
      });

      return state.fetchPromise;
    }

    getPageProductIds() {
      return new Set(
        Array.from(document.querySelectorAll('product-card[data-product-id]'))
          .filter((card) => !this.contains(card))
          .map((card) => card.dataset.productId)
          .filter(Boolean)
      );
    }

    getRenderedCards() {
      if (!this.grid) return [];
      return Array.from(this.grid.querySelectorAll(':scope > [data-feed-card]'));
    }

    prepareCard(card, state) {
      const rank = Number.parseInt(card.dataset.feedRank, 10);
      if (this.showRankBadges && rank > 0 && rank <= 3) {
        const badge = document.createElement('span');
        badge.className = 'home-product-feed__rank';
        badge.textContent = `#${rank}`;
        badge.setAttribute('aria-hidden', 'true');
        const rankLabel = document.createElement('span');
        rankLabel.className = 'visually-hidden';
        rankLabel.textContent = `Rank #${rank} in ${state.label}`;
        card.prepend(rankLabel, badge);
      }
      this.uniquifyCardIds(card, state);
    }

    uniquifyCardIds(card, state) {
      const idMap = new Map();
      card.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        state.idSequence += 1;
        const newId = `${oldId}--${this.instanceKey}-${state.idSequence}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });
      if (!idMap.size) return;

      const referenceAttributes = ['for', 'form', 'aria-controls', 'aria-describedby', 'aria-labelledby', 'aria-owns'];
      card.querySelectorAll(referenceAttributes.map((name) => `[${name}]`).join(',')).forEach((element) => {
        referenceAttributes.forEach((name) => {
          if (!element.hasAttribute(name)) return;
          const value = element.getAttribute(name).split(/\s+/).map((id) => idMap.get(id) || id).join(' ');
          element.setAttribute(name, value);
        });
      });
      card.querySelectorAll('[href^="#"]').forEach((element) => {
        const targetId = element.getAttribute('href').slice(1);
        if (idMap.has(targetId)) element.setAttribute('href', `#${idMap.get(targetId)}`);
      });
    }

    setBusy(isBusy, showSkeleton = false) {
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      if (this.skeleton) this.skeleton.hidden = this.hasSettled ? !(isBusy && showSkeleton) : false;
      if (this.loadMoreButton) {
        this.loadMoreButton.classList.toggle('is-loading', isBusy);
        this.loadMoreButton.setAttribute('aria-disabled', isBusy ? 'true' : 'false');
      }
      if (isBusy) this.announce('Loading products');
    }

    updateLoadMore(state) {
      if (!this.actions) return;
      const hasMore = state.queue.length > 0 || Boolean(state.nextUrl);

      if (this.isProgressiveLoadMode()) {
        const reachedLimit = state.renderedCount >= this.mobileAutoLimit && hasMore;
        const shouldShowViewAll = reachedLimit && this.mobileQuery.matches;
        this.loadMoreButton.hidden = true;
        if (this.viewAllLink) {
          this.viewAllLink.hidden = !shouldShowViewAll;
          this.viewAllLink.href = state.collectionUrl;
        }
        if (!shouldShowViewAll && this.actions.contains(document.activeElement)) {
          this.panel?.focus({ preventScroll: true });
        }
        this.actions.hidden = !shouldShowViewAll;
        return;
      }

      this.loadMoreButton.hidden = false;
      if (this.viewAllLink) this.viewAllLink.hidden = true;
      const shouldHide = this.layout === 'shelf' || !hasMore;
      if (shouldHide && this.actions.contains(document.activeElement)) {
        this.panel?.focus({ preventScroll: true });
      }
      this.actions.hidden = shouldHide;
    }

    isProgressiveLoadMode() {
      return this.mobileAutoLoad && (this.layout === 'shelf' || this.mobileQuery.matches);
    }

    handleGridScroll() {
      this.updateShelfNavigation();
      if (!this.isProgressiveLoadMode() || !this.activeTab) return;
      const state = this.states.get(this.activeTab.dataset.feedKey);
      if (state) state.scrollPosition = this.grid.scrollLeft;
      this.scheduleMobileAutoLoad();
    }

    scheduleMobileAutoLoad() {
      if (this.mobileLoadFrame) return;
      this.mobileLoadFrame = window.requestAnimationFrame(() => {
        this.mobileLoadFrame = null;
        this.maybeAutoLoadMobile();
      });
    }

    async maybeAutoLoadMobile() {
      if (!this.isConnected || !this.hasActivated || !this.isProgressiveLoadMode() || !this.activeTab
        || this.getAttribute('aria-busy') === 'true' || this.errorMessage?.hidden === false) return;
      const key = this.activeTab.dataset.feedKey;
      const state = this.states.get(key);
      if (!state || state.renderedCount >= this.mobileAutoLimit) {
        if (state) this.updateLoadMore(state);
        return;
      }
      if (state.queue.length === 0 && !state.nextUrl) {
        this.updateLoadMore(state);
        return;
      }

      const cards = this.getRenderedCards();
      if (!cards.length) return;
      const gridBounds = this.grid.getBoundingClientRect();
      if (!gridBounds.width || this.grid.hidden) return;
      const lastBounds = cards[cards.length - 1].getBoundingClientRect();
      // A fast swipe can skip an intersecting trigger card. Measure the real
      // rail end instead, excluding the temporary loading tile.
      const remaining = getComputedStyle(this.grid).direction === 'rtl'
        ? gridBounds.left - lastBounds.left
        : lastBounds.right - gridBounds.right;
      if (remaining > Math.max(gridBounds.width, lastBounds.width * 3)) return;

      await this.loadMobileBatch(key);
    }

    async loadMobileBatch(key) {
      const loadSequence = ++this.mobileLoadSequence;
      this.showMobileLoadingTile();
      try {
        await this.loadBatch(key, true, { mobileAuto: true });
      } finally {
        if (loadSequence === this.mobileLoadSequence) this.hideMobileLoadingTile();
      }
    }

    showMobileLoadingTile() {
      if (!this.grid || !this.isProgressiveLoadMode()) return;
      if (!this.mobileLoadingTile) {
        const tile = document.createElement('li');
        tile.className = 'home-product-feed__mobile-loader';
        tile.setAttribute('aria-hidden', 'true');

        const spinner = document.createElement('span');
        spinner.className = 'home-product-feed__mobile-loader-spinner';
        const label = document.createElement('span');
        label.className = 'home-product-feed__mobile-loader-label';
        label.textContent = 'Loading more…';
        tile.append(spinner, label);
        this.mobileLoadingTile = tile;
      }
      this.grid.append(this.mobileLoadingTile);
    }

    hideMobileLoadingTile() {
      this.mobileLoadingTile?.remove();
    }

    updateResponsiveLoadingMode() {
      this.mobileLoadSequence += 1;
      this.hideMobileLoadingTile();
      if (!this.activeTab) return;
      const state = this.states.get(this.activeTab.dataset.feedKey);
      if (state) this.updateLoadMore(state);
      this.updateShelfNavigation(true);
      this.scheduleMobileAutoLoad();
    }

    revealAppendedProducts(firstNewCard, scrollLeftBeforeRequest) {
      if (!this.mobileCarousel || !this.grid || !window.matchMedia('(max-width: 768.98px)').matches) return;
      if (this.revealFrame) window.cancelAnimationFrame(this.revealFrame);
      this.revealFrame = window.requestAnimationFrame(() => {
        this.revealFrame = null;
        if (!firstNewCard.isConnected) return;
        if (Math.abs(this.grid.scrollLeft - scrollLeftBeforeRequest) > 4) return;
        firstNewCard.scrollIntoView({
          inline: 'start',
          block: 'nearest',
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      });
    }

    scrollShelf(direction) {
      if (this.layout !== 'shelf' || !this.grid) return;
      this.grid.scrollBy({
        left: direction * Math.max(240, this.grid.clientWidth * 0.9),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    updateShelfNavigation(afterRender = false) {
      if (this.layout !== 'shelf' || !this.grid) return;
      const update = () => {
        const maxScroll = Math.max(0, this.grid.scrollWidth - this.grid.clientWidth);
        if (this.previousButton) this.previousButton.disabled = this.grid.scrollLeft <= 2;
        if (this.nextButton) this.nextButton.disabled = this.grid.scrollLeft >= maxScroll - 2;
      };
      if (afterRender) window.requestAnimationFrame(update);
      else update();
    }

    resetMobileCarouselPosition() {
      if (!this.grid || (this.layout !== 'shelf' && (!this.mobileCarousel || !this.mobileQuery.matches))) return;
      this.grid.scrollLeft = 0;
    }

    restoreMobileCarouselPosition(scrollPosition = 0) {
      if (!this.grid || (this.layout !== 'shelf' && (!this.mobileCarousel || !this.mobileQuery.matches))) return;
      window.requestAnimationFrame(() => {
        if (this.grid) this.grid.scrollLeft = scrollPosition;
        this.updateShelfNavigation();
        this.scheduleMobileAutoLoad();
      });
    }

    hideMessages() {
      if (this.emptyMessage) this.emptyMessage.hidden = true;
      if (this.errorMessage) this.errorMessage.hidden = true;
    }

    showEmpty(state) {
      this.hasSettled = true;
      this.grid.hidden = true;
      this.skeleton.hidden = true;
      if (this.actions) this.actions.hidden = true;
      this.emptyMessage.hidden = false;
      this.emptyMessage.querySelector('a').href = state.collectionUrl;
      this.announce('No available products found');
    }

    showError(state) {
      this.hasSettled = true;
      this.skeleton.hidden = true;
      if (this.actions) this.actions.hidden = true;
      this.errorMessage.hidden = false;
      this.errorCollectionLink.href = state.collectionUrl;
      this.announce('Products could not be loaded');
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  customElements.define('home-product-feed', HomeProductFeed);
}
